import { html, LitElement } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { styleMap } from 'lit/directives/style-map.js'
import encodeQR from 'qr'
import { observe } from '../common/decorators/observe.js'
import componentStyle from '../common/styles/Component.css'
import style from './Qrcode.css'

const correctionMap: Record<'L' | 'M' | 'Q' | 'H', 'low' | 'medium' | 'quartile' | 'high'> = {
  L: 'low',
  M: 'medium',
  Q: 'quartile',
  H: 'high',
}

/**
 * QR Code component is used for providing information or links
 * to users which they can quickly scan with their smartphone.
 *
 * @status ready
 * @category content
 * @displayName QR Code
 */
@customElement('nord-qrcode')
export default class Qrcode extends LitElement {
  static styles = [componentStyle, style]

  @query('canvas', true) private canvas!: HTMLCanvasElement
  @query('.computed', true) private computed!: HTMLElement

  /**
   * The value of the QR Code, most commonly an URL.
   */
  @property({ reflect: true }) value = ''

  /**
   * Label used by assistive technology. If unspecified, the value will
   * be used instead.
   */
  @property({ reflect: true }) label = ''

  /**
   * The size of the rendered QR Code in pixels.
   */
  @property({ reflect: true, type: Number }) size = 128

  /**
   * The fill color of the QR Code.
   * Can accept any valid CSS color value, including custom properties.
   */
  @property({ reflect: true }) color = 'var(--n-color-text)'

  /**
   * The background color of the QR Code.
   * Can accept any valid CSS color value, including custom properties.
   */
  @property({ reflect: true }) background = 'var(--n-color-surface)'

  /**
   * Error correction level makes the QR Code bigger and helps users to
   * scan it without issues. L, M, Q and H values will use 7%, 15%, 25%
   * and 30% of the QR code for error correction respectively.
   */
  @property({ reflect: true }) correction: 'L' | 'M' | 'Q' | 'H' = 'H'

  firstUpdated() {
    this.generate()
  }

  @observe('correction')
  @observe('color')
  @observe('background')
  @observe('size')
  @observe('value')
  private generate() {
    /**
     * Lit’s built-in hasUpdated property returns true if the component
     * has updated at least once.
     */
    if (!this.hasUpdated) {
      return
    }

    const { backgroundColor, color } = getComputedStyle(this.computed)

    const qrArray = encodeQR(this.value, 'raw', {
      ecc: correctionMap[this.correction],
      border: 0,
    })

    // Draw the canvas 2x larger to avoid blurring on high DPI displays
    const size = this.size * 2
    const ctx = this.canvas.getContext('2d')
    const scale = size / qrArray.length

    this.canvas.width = size
    this.canvas.height = size

    if (ctx) {
      ctx.fillStyle = backgroundColor
      ctx.fillRect(0, 0, size, size)
      qrArray.forEach((row, y) => {
        row.forEach((cell, x) => {
          if (cell) {
            ctx.fillStyle = color
            ctx.fillRect(x * scale, y * scale, scale, scale)
          }
        })
      })
    }
  }

  render() {
    return html`
      <div
        class="n-qrcode"
        style=${styleMap({
          'inline-size': `${this.size}px`,
          'block-size': `${this.size}px`,
        })}
      >
        <canvas role="img" aria-label=${this.label.length > 0 ? this.label : this.value}></canvas>
      </div>
      <div
        class="computed"
        hidden
        style=${styleMap({
          'background-color': `${this.background}`,
          'color': `${this.color}`,
        })}
      ></div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-qrcode': Qrcode
  }
}
